<template>
  <div class="preview-page">
    <el-card class="preview-toolbar glass-card" shadow="never" :body-style="{ padding: '0' }">
      <div class="toolbar-main">
        <div class="toolbar-title">
          <span class="section-kicker">RELEASE CALENDAR</span>
          <h1>发售预告</h1>
        </div>
      </div>
      <div class="month-strip" aria-label="选择预告月份">
        <el-button
          v-for="month in availableMonths"
          :key="month.key"
          :class="{ 'month-year': month.showYear }"
          size="mini"
          :type="selectedMonth === month.key ? 'primary' : 'default'"
          :plain="selectedMonth !== month.key"
          @click="goToMonth(month.key)"
        >{{ month.label }}</el-button>
      </div>
      <div class="toolbar-foot">
        <div class="month-summary">
          <span><strong>{{ totalCount }}</strong> 部预告</span>
          <span><strong>{{ exactCount }}</strong> 部确定日期</span>
          <span><strong>{{ totalCount - exactCount }}</strong> 部仅知旬期</span>
        </div>
        <el-switch v-model="hideAdult" active-text="模糊 R18 封面"></el-switch>
      </div>
    </el-card>

    <div class="preview-body">
      <el-card class="calendar-panel glass-card" shadow="never" :body-style="{ padding: '0', height: '100%' }">
        <div class="panel-heading">
          <div><span class="section-kicker">CALENDAR</span><h2>发售日历</h2></div>
          <span class="panel-hint">点击作品查看当天预告</span>
        </div>
        <div class="calendar-wrap"><FullCalendar ref="calendar" :options="calendarOptions"></FullCalendar></div>
        <div class="calendar-note"><span class="legend-dot"></span> 日历仅标记确定发售日；待定日期可在右侧按旬浏览</div>
      </el-card>

      <el-card class="release-panel glass-card" shadow="never" :body-style="{ padding: '0', height: '100%' }">
        <div class="panel-heading release-heading">
          <div><span class="section-kicker">UPCOMING WORKS</span><h2>本月作品 <small>{{ totalCount }} 部</small></h2></div>
          <el-button v-if="selectedDay" type="text" size="mini" @click="clearDay">返回全部</el-button>
        </div>
        <div class="period-controls">
          <el-radio-group v-model="selectedPeriod" size="mini" @change="changePeriod">
            <el-radio-button label="all">全部 {{ totalCount }}</el-radio-button>
            <el-radio-button label="first">上旬 {{ apiData.first.length }}</el-radio-button>
            <el-radio-button label="middle">中旬 {{ apiData.middle.length }}</el-radio-button>
            <el-radio-button label="last">下旬 {{ apiData.last.length }}</el-radio-button>
          </el-radio-group>
        </div>
        <app-scrollbar ref="releaseScroll" class="release-scroll" v-loading="loading">
          <el-empty v-if="!loading && displayedCount === 0" description="这个时间段还没有收录预告"></el-empty>
          <section v-for="group in displayedGroups" :key="group.key" class="release-group">
            <div class="group-heading"><h3>{{ group.title }}</h3><span>{{ group.items.length }} 部作品</span></div>
            <div v-for="game in group.items" :key="game.id" class="release-item" :class="{ 'release-item--focused': focusedGameId === String(game.id) }" :data-preview-id="game.id">
              <div class="release-cover" :class="{ 'cover-blurred': hideAdult && isAdult(game) }">
                <img v-if="game.imageUrl" :src="imageUrl(game.imageUrl)" :alt="game.name" loading="lazy">
                <span v-else class="cover-placeholder">暂无封面</span>
              </div>
              <div class="release-details">
                <div class="release-topline">
                  <span class="work-id">{{ game.gameId }}</span>
                  <el-tag size="mini" effect="plain">{{ game.gameType || '游戏' }}</el-tag>
                  <el-tag v-if="!isAdult(game)" size="mini" type="success" effect="plain">{{ ageLabel(game) }}</el-tag>
                </div>
                <a class="release-title" :href="game.gameUrl" target="_blank" rel="noopener noreferrer">{{ game.name }}</a>
                <div class="release-meta">
                  <a v-if="game.makerUrl" :href="game.makerUrl" target="_blank" rel="noopener noreferrer">{{ game.makerName }}</a>
                  <span v-else>{{ game.makerName }}</span>
                  <span class="release-date">{{ game.saleDate }}</span>
                </div>
                <div class="release-tags">
                  <el-tag v-for="tag in (game.tags || [])" :key="tag" size="mini" type="info" effect="plain">{{ tag }}</el-tag>
                </div>
                <div class="release-bottom">
                  <strong class="release-price">{{ game.price || '价格未定' }}</strong>
                  <div class="release-actions">
                    <el-button v-if="loggedIn" type="text" size="mini" :icon="game.follow ? 'el-icon-star-on' : 'el-icon-star-off'" @click="toggleFollow(game)">{{ game.follow ? '已收藏' : '收藏' }}</el-button>
                    <el-button v-if="loggedIn" type="text" size="mini" icon="el-icon-remove-outline" @click="blacklistMaker(game)">屏蔽社团</el-button>
                    <el-button type="primary" plain size="mini" :disabled="!game.gameUrl" @click="openGame(game)">查看作品 ↗</el-button>
                  </div>
                </div>
              </div>
            </div>
          </section>
        </app-scrollbar>
      </el-card>
    </div>
  </div>
</template>

<script>
import axios from 'axios'
import FullCalendar from '@fullcalendar/vue'
import dayGridPlugin from '@fullcalendar/daygrid'
import zhCn from '@fullcalendar/core/locales/zh-cn'
import AppScrollbar from '@/components/AppScrollbar.vue'
import { EventBus } from '@/EventBus'

function monthKey(date) {
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`
}

export default {
  name: 'PreviewLatest',
  components: { FullCalendar, AppScrollbar },
  data() {
    const today = new Date()
    const start = new Date(today.getFullYear(), today.getMonth(), 1)
    const end = new Date(today.getFullYear(), today.getMonth() + 12, 1)
    return {
      selectedMonth: monthKey(start),
      rangeStart: start,
      rangeEnd: end,
      apiData: { first: [], middle: [], last: [] },
      selectedPeriod: today.getDate() <= 10 ? 'first' : today.getDate() <= 20 ? 'middle' : 'last',
      selectedDay: '',
      focusedGameId: '',
      hideAdult: false,
      loggedIn: Boolean(localStorage.getItem('token')),
      loading: false,
      requestId: 0,
      calendarApi: null,
      calendarOptions: {
        plugins: [dayGridPlugin],
        locale: zhCn,
        initialView: 'dayGridMonth',
        initialDate: start,
        validRange: { start, end },
        headerToolbar: false,
        fixedWeekCount: false,
        dayMaxEvents: 3,
        eventOrder: 'order',
        eventOrderStrict: true,
        moreLinkContent: info => `+${info.num}`,
        moreLinkClick: this.onMoreLinkClick,
        eventDisplay: 'block',
        eventDidMount: info => { info.el.title = info.event.title },
        events: [],
        datesSet: this.onDatesSet,
        eventClick: this.onEventClick
      }
    }
  },
  computed: {
    availableMonths() {
      return Array.from({ length: 12 }, (_, index) => {
        const date = new Date(this.rangeStart.getFullYear(), this.rangeStart.getMonth() + index, 1)
        const showYear = index === 0 || date.getMonth() === 0
        return { key: monthKey(date), showYear, label: showYear ? `${date.getFullYear()} 年 ${date.getMonth() + 1} 月` : `${date.getMonth() + 1} 月` }
      })
    },
    allGames() {
      return [...this.apiData.first, ...this.apiData.middle, ...this.apiData.last]
    },
    totalCount() { return this.allGames.length },
    exactCount() { return this.allGames.filter(game => this.exactDate(game.saleDate)).length },
    calendarEvents() {
      return this.allGames.map((game, index) => {
        const date = this.exactDate(game.saleDate)
        return date ? {
          id: String(game.id),
          title: game.name,
          start: date,
          allDay: true,
          order: index,
          extendedProps: { releaseDay: date, previewId: String(game.id) }
        } : null
      }).filter(Boolean)
    },
    displayedGroups() {
      if (this.selectedDay) {
        const date = this.selectedDay
        return [{ key: date, title: `${Number(date.slice(5, 7))} 月 ${Number(date.slice(8, 10))} 日`, items: this.allGames.filter(game => this.exactDate(game.saleDate) === date) }]
      }
      const groups = [
        { key: 'first', title: '上旬 · 1—10 日', items: this.apiData.first },
        { key: 'middle', title: '中旬 · 11—20 日', items: this.apiData.middle },
        { key: 'last', title: '下旬 · 21 日起', items: this.apiData.last }
      ]
      return this.selectedPeriod === 'all' ? groups : groups.filter(group => group.key === this.selectedPeriod)
    },
    displayedCount() {
      return this.displayedGroups.reduce((sum, group) => sum + group.items.length, 0)
    }
  },
  watch: {
    calendarEvents(events) {
      if (!this.calendarApi) return
      this.calendarApi.removeAllEvents()
      this.calendarApi.addEventSource(events)
    }
  },
  mounted() {
    this.calendarApi = this.$refs.calendar.getApi()
    this.calendarApi.removeAllEvents()
    this.calendarApi.addEventSource(this.calendarEvents)
    this.refreshHandler = () => this.loadMonth(this.selectedMonth)
    this.loginHandler = () => { this.loggedIn = Boolean(localStorage.getItem('token')) }
    EventBus.$on('refreshPreviewList', this.refreshHandler)
    EventBus.$on('changeState', this.loginHandler)
    if (!this.loading && !this.requestId) this.loadMonth(this.selectedMonth)
  },
  beforeDestroy() {
    this.requestId += 1
    EventBus.$off('refreshPreviewList', this.refreshHandler)
    EventBus.$off('changeState', this.loginHandler)
  },
  methods: {
    async loadMonth(key) {
      const id = ++this.requestId
      this.loading = true
      this.selectedDay = ''
      this.focusedGameId = ''
      try {
        const [year, month] = key.split('-').map(Number)
        const token = localStorage.getItem('token')
        const response = await axios.post('/api/open/preview', { year, month }, { headers: { Authorization: token ? `Bearer ${token}` : '' } })
        if (response.data.code !== 200 || !response.data.data) throw new Error('加载失败')
        if (id !== this.requestId) return
        const data = response.data.data
        this.apiData = { first: data.first || [], middle: data.middle || [], last: data.last || [] }
        this.resetListScroll()
      } catch (error) {
        if (id === this.requestId) this.$message.error('预告加载失败，请稍后重试')
      } finally {
        if (id === this.requestId) this.loading = false
      }
    },
    onDatesSet(info) {
      const key = monthKey(info.view.currentStart)
      if (key !== this.selectedMonth) {
        this.selectedMonth = key
        this.selectedPeriod = 'all'
      }
      if (this.requestedMonth !== key) {
        this.requestedMonth = key
        this.loadMonth(key)
      }
    },
    onEventClick(info) {
      info.jsEvent.preventDefault()
      this.showDay(info.event.extendedProps.releaseDay, info.event.extendedProps.previewId)
    },
    onMoreLinkClick(info) {
      info.jsEvent.preventDefault()
      this.showDay(monthKey(info.date) + `-${String(info.date.getDate()).padStart(2, '0')}`)
      // FullCalendar treats an empty return as a request to open its popover.
      return true
    },
    goToMonth(key) {
      if (!key || !this.availableMonths.some(item => item.key === key)) return
      this.selectedMonth = key
      this.selectedPeriod = 'all'
      this.selectedDay = ''
      this.focusedGameId = ''
      if (this.calendarApi) this.calendarApi.gotoDate(`${key}-01`)
    },
    changePeriod() {
      this.selectedDay = ''
      this.focusedGameId = ''
      this.resetListScroll()
    },
    clearDay() {
      this.selectedDay = ''
      this.focusedGameId = ''
      this.selectedPeriod = 'all'
      this.resetListScroll()
    },
    showDay(date, gameId = '') {
      this.selectedDay = date
      this.focusedGameId = gameId || ''
      this.$nextTick(() => {
        const list = this.$refs.releaseScroll && this.$refs.releaseScroll.$el
        if (!list) return
        if (!gameId) {
          list.scrollTop = 0
          return
        }
        const item = Array.from(list.querySelectorAll('.release-item')).find(element => element.dataset.previewId === String(gameId))
        if (!item) return
        const mobile = window.matchMedia('(max-width: 1150px)').matches
        const scroller = mobile ? this.$el : list
        scroller.scrollTop += item.getBoundingClientRect().top - scroller.getBoundingClientRect().top - 14
      })
    },
    resetListScroll() {
      this.$nextTick(() => {
        if (this.$refs.releaseScroll) this.$refs.releaseScroll.$el.scrollTop = 0
      })
    },
    exactDate(value) {
      const match = String(value || '').match(/(\d{4})年(\d{1,2})月(\d{1,2})日/)
      if (!match) return ''
      return `${match[1]}-${String(match[2]).padStart(2, '0')}-${String(match[3]).padStart(2, '0')}`
    },
    imageUrl(value) { return value && value.startsWith('//') ? `https:${value}` : value },
    isAdult(game) {
      const genre = game.genre || []
      return !genre.includes('全年齢') && !genre.includes('R-15')
    },
    ageLabel(game) { return (game.genre || []).includes('全年齢') ? '全年龄' : 'R-15' },
    openGame(game) {
      if (game.gameUrl) window.open(game.gameUrl, '_blank', 'noopener,noreferrer')
    },
    async toggleFollow(game) {
      const token = localStorage.getItem('token')
      if (!token) return
      try {
        const response = game.follow
          ? await axios.delete('/api/user/announce', { headers: { Authorization: `Bearer ${token}` }, data: { id: game.id } })
          : await axios.post('/api/user/announce', { id: game.id }, { headers: { Authorization: `Bearer ${token}` } })
        if (response.data.code !== 200) throw new Error(response.data.message || '操作失败')
        game.follow = !game.follow
        this.$message.success(game.follow ? '已加入收藏' : '已取消收藏')
      } catch (error) {
        this.$message.error(error.message || '收藏操作失败')
      }
    },
    async blacklistMaker(game) {
      const token = localStorage.getItem('token')
      if (!token || !game.makerId) return
      try {
        await this.$confirm(`屏蔽「${game.makerName}」的预告作品？`, '屏蔽社团', { type: 'warning' })
        const response = await axios.post('/api/user/author/blacklist', { id: game.makerId }, { headers: { Authorization: `Bearer ${token}` } })
        if (response.data.code !== 200) throw new Error(response.data.message || '屏蔽失败')
        this.$message.success('已屏蔽该社团')
        this.loadMonth(this.selectedMonth)
      } catch (error) {
        if (error !== 'cancel' && error !== 'close') this.$message.error(error.message || '屏蔽失败')
      }
    }
  }
}
</script>

<style scoped>
.preview-page { display: flex; flex-direction: column; gap: 10px; height: 100%; min-height: 0; color: #303133; text-align: left; }
.glass-card { border: 1px solid rgba(220, 223, 230, .8); border-radius: 17px; background: rgba(255, 255, 255, .58); box-shadow: 0 10px 30px rgba(0, 0, 0, .08); backdrop-filter: blur(8px); -webkit-backdrop-filter: blur(8px); }
.section-kicker { color: #409eff; font-size: 10px; font-weight: 800; letter-spacing: .16em; }
.preview-toolbar { flex: none; }
.preview-toolbar >>> .el-card__body { padding: 14px 18px 12px !important; }
.toolbar-main, .toolbar-foot, .panel-heading, .release-bottom { display: flex; align-items: center; justify-content: space-between; gap: 12px; }
.toolbar-title h1 { margin: 3px 0 0; font-size: 23px; line-height: 1.25; }
.month-strip { display: flex; gap: 6px; overflow-x: auto; padding: 10px 0; scrollbar-width: thin; scrollbar-color: #409eff transparent; }
.month-strip .el-button { flex: 1 0 57px; min-width: 57px; margin: 0; padding: 7px 5px; }
.month-strip .el-button.month-year { flex-basis: 108px; min-width: 108px; }
.toolbar-foot { border-top: 1px solid rgba(220, 223, 230, .7); padding-top: 10px; }
.month-summary { display: flex; flex-wrap: wrap; gap: 9px 18px; color: #909399; font-size: 11px; }
.month-summary strong { color: #409eff; font-size: 15px; font-variant-numeric: tabular-nums; }
.preview-body { display: grid; grid-template-columns: minmax(340px, .88fr) minmax(0, 1.12fr); gap: 10px; flex: 1; min-height: 0; }
.calendar-panel, .release-panel { min-width: 0; min-height: 0; overflow: hidden; }
.calendar-panel >>> .el-card__body, .release-panel >>> .el-card__body { display: flex; flex-direction: column; min-height: 0; }
.panel-heading { flex: none; padding: 16px 18px 11px; border-bottom: 1px solid rgba(220, 223, 230, .7); }
.panel-heading h2 { margin: 4px 0 0; font-size: 18px; line-height: 1.25; }
.panel-heading h2 small { color: #909399; font-size: 11px; font-weight: 400; }
.panel-hint { color: #909399; font-size: 11px; }
.calendar-wrap { flex: 1; min-height: 0; padding: 12px 14px; }
.calendar-wrap >>> .fc { height: 100%; font-size: 12px; --fc-border-color: rgba(220, 223, 230, .68); --fc-today-bg-color: rgba(64, 158, 255, .09); --fc-event-bg-color: #409eff; --fc-event-border-color: #409eff; }
.calendar-wrap >>> .fc .fc-daygrid-day-number { color: #606266; text-decoration: none; }
.calendar-wrap >>> .fc .fc-col-header-cell-cushion { color: #909399; text-decoration: none; }
.calendar-wrap >>> .fc .fc-event { cursor: pointer; border-radius: 4px; font-size: 10px; }
.calendar-wrap >>> .fc .fc-event-main { overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.calendar-wrap >>> .fc .fc-daygrid-more-link { color: #409eff; font-size: 11px; font-weight: 700; }
.calendar-note { flex: none; padding: 9px 15px 12px; border-top: 1px solid rgba(220, 223, 230, .6); color: #909399; font-size: 11px; }
.legend-dot { display: inline-block; width: 8px; height: 8px; margin-right: 5px; border-radius: 50%; background: #409eff; }
.period-controls { flex: none; overflow-x: auto; padding: 11px 15px 9px; }
.period-controls >>> .el-radio-group { display: flex; min-width: max-content; }
.period-controls >>> .el-radio-button__inner { padding: 7px 10px; }
.release-scroll { flex: 1; min-height: 0; padding: 0 14px 14px; }
.release-group + .release-group { margin-top: 8px; }
.group-heading { display: flex; align-items: center; justify-content: space-between; gap: 8px; margin: 0 2px 9px; padding: 9px 2px; border-bottom: 1px solid rgba(220, 223, 230, .7); }
.group-heading h3 { margin: 0; font-size: 13px; }
.group-heading span { color: #909399; font-size: 11px; }
.release-item { display: flex; gap: 12px; min-width: 0; margin-bottom: 9px; padding: 10px; border: 1px solid rgba(220, 223, 230, .8); border-radius: 12px; background: rgba(255, 255, 255, .48); }
.release-cover { display: flex; align-items: center; justify-content: center; flex: 0 0 176px; width: 176px; height: 132px; overflow: hidden; border-radius: 8px; background: rgba(220, 223, 230, .35); }
.release-cover img { width: 100%; height: 100%; object-fit: cover; }
.cover-blurred img { filter: blur(9px); }
.cover-placeholder { color: #909399; font-size: 11px; }
.release-details { display: flex; flex-direction: column; gap: 5px; min-width: 0; flex: 1; }
.release-item--focused { border-color: #409eff; box-shadow: 0 0 0 2px rgba(64, 158, 255, .16); }
.release-topline, .release-meta, .release-tags, .release-actions { display: flex; align-items: center; gap: 6px; min-width: 0; }
.work-id { color: #409eff; font-size: 10px; font-weight: 700; letter-spacing: .04em; }
.release-title { overflow: hidden; color: #303133; font-size: 13px; font-weight: 700; line-height: 1.45; text-decoration: none; display: -webkit-box; -webkit-box-orient: vertical; -webkit-line-clamp: 2; }
.release-title:hover, .release-meta a:hover { color: #409eff; }
.release-meta { justify-content: space-between; color: #909399; font-size: 11px; }
.release-meta a { min-width: 0; overflow: hidden; color: #606266; text-decoration: none; text-overflow: ellipsis; white-space: nowrap; }
.release-date { flex: none; }
.release-tags { min-height: 19px; flex-wrap: wrap; overflow: visible; }
.release-tags >>> .el-tag { max-width: 100%; height: auto; line-height: 17px; padding-top: 1px; padding-bottom: 1px; overflow-wrap: anywhere; white-space: normal; }
.release-bottom { margin-top: auto; }
.release-price { color: #409eff; font-size: 14px; white-space: nowrap; }
.release-actions { justify-content: flex-end; flex-wrap: wrap; gap: 4px; }
.release-actions .el-button { margin: 0; }
@media (max-width: 1150px) {
  .preview-page { overflow-y: auto; }
  .preview-body { display: flex; flex-direction: column; flex: none; min-height: auto; }
  .calendar-panel >>> .el-card__body { height: auto !important; }
  .calendar-wrap { height: 450px; flex: none; }
  .release-panel >>> .el-card__body { height: auto !important; }
  .release-scroll { overflow: visible; flex: none; }
}
@media (max-width: 767px) {
  .preview-toolbar >>> .el-card__body { padding: 12px !important; }
  .toolbar-main { align-items: flex-start; flex-wrap: wrap; }
  .toolbar-title h1 { font-size: 20px; }
  .month-summary { gap: 8px 12px; }
  .toolbar-foot { align-items: flex-start; flex-wrap: wrap; }
  .calendar-wrap { height: 360px; padding: 7px; }
  .calendar-note { line-height: 1.5; }
  .panel-heading { padding: 12px; }
  .panel-heading h2 { font-size: 16px; }
  .panel-hint { font-size: 10px; }
  .release-scroll { padding: 0 8px 8px; }
  .release-item { gap: 9px; padding: 8px; }
  .release-cover { flex-basis: 124px; width: 124px; height: 96px; }
  .release-title { font-size: 12px; }
  .release-meta { flex-wrap: wrap; gap: 3px; font-size: 10px; }
  .release-actions .el-button { padding: 4px; font-size: 10px; }
  .release-price { font-size: 12px; }
}
</style>
